import { useReducer, useCallback } from "react";

/** @typedef {{pageIndex?:number, pageSize?:number, totalCount?:number, totalPage?:number}} Pagination */
/** @typedef {{list:any[], pagination:Pagination}} ListState */

const SET_LIST = "SET_LIST";
const ADD_ITEMS = "ADD_ITEMS";
const UPDATE_ITEM = "UPDATE_ITEM";


/**
 * 初始狀態：包含空的列表與預設分頁資料
 */
const initialState = {
	list: [], // 資料陣列
	pagination: {
		// 分頁資訊
		pageIndex: 1, // 當前頁數
		pageSize: 50, // 每頁顯示的數量
		totalCount: 0, // 總共多少筆資料
		totalPage: 1 // 總頁數
	},
	meta: {} //額外
};

/**
 * @param {ListState} state
 * @param {{type:string, payload: { list?:any[], pagination?:Pagination, id?:string, data?:object, rest?:object }}} action
 * @returns {ListState}
 */
const listReducer = (state, action) => {
	const { list, pagination,  id, data, meta } = action.payload || {};

	switch (action.type) {
		case SET_LIST:
			return {
				...state,
				list: list || [],
				pagination: { ...pagination },
				meta
			};
		case ADD_ITEMS:
			return {
				...state,
				list: Array.isArray(state.list) && Array.isArray(list)
					? [...state.list, ...list]
					: state.list,
				pagination: { ...pagination },
				meta
			};
		case UPDATE_ITEM:
			return {
				...state,
				list: state.list.map((item) =>
					item.id === id ? { ...item, ...(data || {}) } : item
				),
				meta
			};
		default:
			return state;
	}
};


/**
 * 自定義 Hook：專門處理列表資料與分頁的狀態管理
 *
 * @param {object} initial - 初始狀態，可覆寫預設的 initialState
 * @returns {{
 *   listState: ListState,
 *   setList: (list:any[], pagination:Pagination, rest?:object) => void,
 *   addItems: (list:any[], pagination:Pagination, rest?:object) => void,
 *   updateItem: (id:string, data:object) => void
 * }}
 */
const useListState = (initial = initialState) => {
	const [listState, dispatchList] = useReducer(listReducer, initial);

	const setList = useCallback((list, pagination, meta = {}) => {
		dispatchList({ type: SET_LIST, payload: { list, pagination, meta } });
	}, []);

	const addItems = useCallback((list, pagination, meta = {}) => {
		dispatchList({ type: ADD_ITEMS, payload: { list, pagination, meta } });
	}, []);

	const updateItem = useCallback((id, data) => {
		dispatchList({ type: UPDATE_ITEM, payload: { id, data } });
	}, []);

	return {
		listState,
		setList,
		addItems,
		updateItem
	};
};

export default useListState;